import type { FC } from 'react';
import React from 'react';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { VIEW_REACTIONS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceStop } from '@confluence/experience-tracker/entry-points/component';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import type { ReactionLocation } from './types';
import { i18n } from './utils/i18n';

type ContainerProps = {
	location: ReactionLocation;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<ContainerProps>({
	position: 'relative',
	cursor: 'not-allowed',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingLeft: ({ location }) =>
		location === 'page-comment' ||
		location === 'inline-comment' ||
		location === 'editor-inline-comment'
			? '4px'
			: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	top: ({ location }) =>
		location === 'inline-comment' ||
		location === 'editor-inline-comment' ||
		location === 'page-comment'
			? '4px'
			: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ location }) =>
		location === 'inline-comment' || location === 'editor-inline-comment'
			? token('color.text.subtlest')
			: token('color.text.subtle'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	fontSize: ({ location }) =>
		location === 'inline-comment' || location === 'editor-inline-comment' ? '12px' : 'inherit',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.span<ContainerProps>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	top: ({ location }) => (location === 'page' ? '-5px' : '-6px'),
});

type ReactionsViewErrorProps = ContainerProps & { error: Error };

export const ReactionsViewError: FC<ReactionsViewErrorProps> = ({ error, location }) => {
	const isComment =
		location === 'page-comment' ||
		location === 'inline-comment' ||
		location === 'editor-inline-comment';

	// COMMENTS-352 - If we run into a permissions view error, we need to mark it as handled
	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	return (
		<ErrorDisplay error={error}>
			<ExperienceStop name={VIEW_REACTIONS_EXPERIENCE} error={error} />
			<Container location={location}>
				<FormattedMessage {...(isComment ? i18n.viewError : i18n.viewErrorTooltip)}>
					{(tooltipText) => (
						<Tooltip content={tooltipText} position="top-start">
							<StatusErrorIcon
								color={
									location === 'inline-comment' || location === 'editor-inline-comment'
										? token('color.icon.accent.gray')
										: token('color.icon.subtle')
								}
								label="Icon showing an error with rendering reactions"
								spacing="spacious"
							/>
							{!isComment && (
								<TextContainer location={location}>
									<FormattedMessage {...i18n.viewError} />
								</TextContainer>
							)}
						</Tooltip>
					)}
				</FormattedMessage>
			</Container>
		</ErrorDisplay>
	);
};
